import { useEffect } from "react";
import { useState } from "react";
import { Alert, LinearProgress } from "@mui/material";
import APIRequest, { APIError } from "../../system/APIRequest";
import { FilterableResourceTable } from "../../components/adminResources/FilterableResourceTable";
import i18n from "../../translation";
import { useNavigate, useParams } from "react-router-dom";

export interface APIResource {
  name: string;
  endpoint: string;
  has_entries: boolean;
  sharable: boolean;
  permissions: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  }
}

// These will override columns shown for a given endpoint
// Useful to clean up certain objects
const columnOverrides: { [key: string]: string[]; } = {
  "user": [
    "_account",
    "email",
    "name",
    "created_at",
    "role",
    "session_hours_last_30_days",
    "logins_last_30_days",
    "api_tokens_last_30_days",
    "last_active",
    "last_login"
  ],
  "account": [
    "name",
    "created_at",
    "active_users",
    "hubspot_company",
    "plan",
    "social_plan",
    "addon_plans",
    "session_hours_last_30_days",
    "logins_last_30_days",
    "api_tokens_last_30_days",
    "parent_account_name"
  ],
  "billing-order": [
    "_account",
    "account_name",
    "created_at",
    "approved_at",
    "signed_at",
    "terminated_at",
    "subscription_value",
    "onetime_value",
    "scheduled_termination",
    "next_invoice",
    "previous_invoice",
    "is_active_on_account"
  ]
}

export default function ResourceManagementPage(props: any) {
  let navigate = useNavigate();
  const { endpoint } = useParams<any>();

  const [resourceListError, setResourceListError] = useState<APIError | undefined>(undefined);
  const [apiResourceList, setApiResourceList] = useState<Array<APIResource>>([]);
  const [selectedObject, setSelectedObject] = useState<APIResource | null>(null);

  useEffect(() => {
    if (apiResourceList.length > 0)
      return;

    APIRequest("obj").then((response: any) => {
      let resources: Array<APIResource> = [];
      response['output'].forEach((obj: any) => {
        resources.push(obj);
      });

      resources.sort((a: APIResource, b: APIResource) => {
        return a.name.localeCompare(b.name);
      });

      setApiResourceList(resources);
    }).catch((reason: APIError) => {
      setResourceListError(reason);
    });
  }, [apiResourceList.length, endpoint, selectedObject]);

  if (endpoint !== selectedObject?.endpoint && apiResourceList.length > 0) {
    // Ensure the endpoint we've navigated to matches the selected
    const matchedResource = apiResourceList.find((value: APIResource) => value.endpoint === endpoint) || null;
    if (matchedResource === null) {
      navigate("/admin/resources");
    } else {
      setSelectedObject(matchedResource);
    }
    return <div />; // Nothing to get a re-render
  }

  // If we have an error, just show that
  if (resourceListError) {
    return <Alert severity="error">
      <p>{resourceListError.message || resourceListError.server_message} ({resourceListError.code})</p>
    </Alert>
  }
  // No error, but the list is empty - assume it is loading
  else if (!apiResourceList.length) {
    return <div style={{ padding: "2rem" }}>
      <LinearProgress />
      <p>{i18n.t("loading")}...</p>
    </div>;
  }

  const visibleColumns = selectedObject !== null ? columnOverrides[selectedObject.endpoint] || undefined : undefined;

  return (
    <div>
      {selectedObject &&
        <FilterableResourceTable
          endpoint={selectedObject.endpoint}
          displayName={selectedObject.name}
          asAdmin={props?.asAdmin}
          visibleColumns={visibleColumns}
          /* Disable create here for users, as we want the button on the Account page to avoid issues */
          allowCreate={selectedObject?.endpoint !== "user"} />
      }
    </div>
  );
}